// content/bosses/boss1.ts — BOSS 1: THE BROODGATE (DESIGN §5, Zone 1).
//
//   "Verb: target priority at boss scale — the boss is a generator network."
//
// A living gate of bone and mortar fused into the arena's north wall. Three
// spawn-maws at staggered heights pour the zone's own roster; the Heart behind
// them only answers a blow while enough maws are down. Maws regrow, so the fight
// is a rhythm the player sets: break the sources, spend the window, break them
// again — everything zone 1 taught, under pressure.
//
// Every literal in this file comes from data/zones/zone1.ts (ZONE1_BOSS). The
// only numbers written here are geometry constants of the drawing.

import type { Draw, RunCtx, Vec } from '../../types'
import { Boss, registerBoss } from '../../game/boss'
import {
  bossDefeated,
  registerBossArena,
  registerZoneContent,
  spawnBossPart,
  type BossPart,
} from '../../game/zones'
import { TILE } from '../../game/world'
import {
  ZONE1_BOSS,
  ZONE1_BOSS_ARENA,
  ZONE1_BOSS_GENERATORS,
  ZONE1_BOSS_PROJECTILES,
} from '../../data/zones/zone1'
import { emit } from '../../engine/particles'
import { registerRecipe, drawSprite } from '../../engine/sprites'

const D = ZONE1_BOSS
const RAD = Math.PI / 180

const tileCenter = (t: { tx: number; ty: number }): Vec => ({
  x: t.tx * TILE + TILE / 2,
  y: t.ty * TILE + TILE / 2,
})

/** Per-phase array read, clamped — phases are 1-based. */
function byPhase<T>(arr: readonly T[], phase: number): T {
  return arr[Math.max(0, Math.min(arr.length - 1, phase - 1))]
}

const dirScratch: Vec = { x: 0, y: 0 }
const posScratch: Vec = { x: 0, y: 0 }

interface Maw {
  part: BossPart | null
  home: Vec
  /** seconds until it comes back; <= 0 and !alive means "not counting yet" */
  regrow: number
  /** which row of the pour table this maw is on */
  cursor: number
  /** open-mouth pose timer, purely visual */
  openT: number
}

class Broodgate extends Boss {
  readonly id = D.id
  /** read structurally by game/hud.ts for the boss bar */
  readonly name = D.name
  readonly phaseTicks = [D.phase3At, D.phase2At]

  private built = false
  private maws: Maw[] = []
  private pourT = 0
  private boltT = 0
  private beamT = 0

  /** live 3-bolt spread windup */
  private boltWind = 0

  // --- the sweeping beam
  private beamState: 'idle' | 'wind' | 'sweep' = 'idle'
  private beamWind = 0
  private beamK = 0
  private beamDirIndex = 0
  /** how many sweeps this activation still owes (P3 sweeps back) */
  private beamSweepsLeft = 0
  private beamAngle = 0
  private beamLen = 0
  private beamTouch = 0

  /** the Heart's shell state, for render + takeDamage */
  private exposed = false
  private pingT = 0
  private hurtT = 0

  private dying = false
  private tookDamage = false
  private lastHp = -1

  constructor() {
    super(D.heartHp)
    this.radius = D.heartRadius
    const c = tileCenter(D.heartTile)
    this.pos.x = c.x
    this.pos.y = c.y
    for (const t of D.mawTiles) {
      this.maws.push({ part: null, home: tileCenter(t), regrow: 0, cursor: 0, openT: 0 })
    }
  }

  // ------------------------------------------------------------------ build

  private build(ctx: RunCtx): void {
    this.built = true
    const def = ZONE1_BOSS_GENERATORS[0]
    for (let i = 0; i < this.maws.length; i++) {
      const m = this.maws[i]
      m.cursor = i
      m.part = spawnBossPart(ctx, def, m.home.x, m.home.y)
      m.part.spriteId = 'broodgate_maw'
    }
    this.pourT = byPhase(D.pourInterval, 1) * 0.6
    this.boltT = byPhase(D.bolt.interval, 1)
    this.lastHp = ctx.player.hp
    ctx.bark('THE BROODGATE. Three mouths and one heart — shut the mouths.')
    ctx.sfx('door')
    ctx.shake(6, 0.7)
  }

  // ------------------------------------------------------------------ state

  private get mawsDown(): number {
    let n = 0
    for (const m of this.maws) if (!m.part || !m.part.alive) n++
    return n
  }

  private liveChildren(ctx: RunCtx): number {
    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) {
      if (list[i].alive && list[i].tags.broodgate !== undefined) n++
    }
    return n
  }

  protected onPhase(phase: number): void {
    const ctx = this.ctx
    if (!ctx) return
    if (phase === 2) {
      ctx.bark('The gate opens its throat. Watch the light.')
      this.beamT = byPhase(D.beam.interval, 2)
    } else if (phase === 3) {
      ctx.bark('It pours everything it has left.')
      this.beamT = Math.min(this.beamT, byPhase(D.beam.interval, 3))
    }
    ctx.shake(5, 0.5)
    ctx.sfx('telegraph')
  }

  // ----------------------------------------------------------------- damage

  /**
   * The Heart is armoured by its own network: while fewer than `exposeAt` maws
   * are down, a blow rings off the shell and says so. The burst cap and the
   * phase plumbing live in the base class and stay untouched.
   */
  takeDamage(n: number): void {
    if (!this.alive || n <= 0) return
    if (!this.exposed) {
      if (this.pingT <= 0) {
        this.ctx?.sfx('ui')
        emit(this.pos.x, this.pos.y + this.radius * 0.4, {
          count: 5,
          color: '#9aa6c4',
          speed: 130,
          life: 0.22,
          size: 3,
          shape: 1,
        })
      }
      this.pingT = 0.16
      return
    }
    const before = this.hp
    super.takeDamage(n)
    if (this.hp < before) this.hurtT = 0.12
    // The phase is a function of the Heart's health, so it turns wherever health
    // turns — not on the next tick. Damage arrives from several places in a step
    // (melee before the boss update, projectile splash after it); without this a
    // reader could catch the boss at 59% and still be told it is in P1.
    this.applyPhase()
  }

  private applyPhase(): void {
    const f = this.healthFraction
    this.setPhase(f <= D.phase3At ? 3 : f <= D.phase2At ? 2 : 1)
  }

  // ----------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.tick(ctx, dt)
    if (!this.built) this.build(ctx)
    if (this.pingT > 0) this.pingT -= dt
    if (this.hurtT > 0) this.hurtT -= dt

    if (this.dying) return
    if (!this.alive) {
      this.die(ctx)
      return
    }

    // no-hit watch: any drop in the Warden's health during the fight, from any
    // source, spends the clean kill. Under `?invuln=1` damagePlayer never
    // reaches the player, so an invulnerable run reads as untouched — true, and
    // deliberately noted where the test asserts it.
    if (this.lastHp >= 0 && ctx.player.hp < this.lastHp) this.tookDamage = true
    this.lastHp = ctx.player.hp

    this.applyPhase()
    const phase = this.phase

    this.updateMaws(ctx, dt, phase)
    this.exposed = this.mawsDown >= byPhase(D.exposeAt, phase)

    this.updatePour(ctx, dt, phase)
    this.updateBolts(ctx, dt, phase)
    this.updateBeam(ctx, dt, phase)
  }

  /** Regrowth: staggered, so the window never slams shut all at once. */
  private updateMaws(ctx: RunCtx, dt: number, phase: number): void {
    const base = byPhase(D.regrowSeconds, phase)
    let regrowing = 0
    for (const m of this.maws) {
      if (m.openT > 0) m.openT -= dt
      if (!m.part) continue
      if (m.part.alive) {
        m.regrow = 0
        // living maws are drawn by the shared generator renderer; the pose is
        // set here so the open mouth lands on the same frame as the pour.
        m.part.spriteId = m.openT > 0 ? 'broodgate_maw_open' : 'broodgate_maw'
        continue
      }
      if (m.regrow <= 0) {
        // just fell: schedule it behind anything already coming back
        m.regrow = base + regrowing * D.regrowStagger
      }
      regrowing++
      m.regrow -= dt
      if (m.regrow <= 0) {
        m.regrow = 0
        m.part.alive = true
        m.part.hp = m.part.maxHp
        ctx.sfx('door')
        ctx.shake(3, 0.25)
        emit(m.home.x, m.home.y, {
          count: 18,
          color: '#d8cfb4',
          speed: 150,
          speedVar: 90,
          life: 0.5,
          lifeVar: 0.2,
          size: 4,
          grav: 120,
        })
      }
    }
  }

  /** Pours: only living maws pour, so cutting one really does stop a stream. */
  private updatePour(ctx: RunCtx, dt: number, phase: number): void {
    this.pourT -= dt
    for (const m of this.maws) {
      if (m.part && m.part.alive) {
        // the shared generator charge-glow reads the boss's own clock
        m.part.timer = Math.max(0, this.pourT)
      }
    }
    if (this.pourT > 0) return
    this.pourT = byPhase(D.pourInterval, phase)

    if (this.liveChildren(ctx) >= D.pourLiveCap) return
    const table = byPhase(D.pourTable, phase)
    const per = byPhase(D.pourCount, phase)

    for (let i = 0; i < this.maws.length; i++) {
      const m = this.maws[i]
      if (!m.part || !m.part.alive) continue
      m.openT = 0.55
      for (let k = 0; k < per; k++) {
        if (this.liveChildren(ctx) >= D.pourLiveCap) return
        const id = table[m.cursor % table.length]
        m.cursor++
        // clear of the gate's shadow, so a poured body is readable the moment
        // it exists
        posScratch.x = m.home.x + (ctx.rng() - 0.5) * 44
        posScratch.y = m.home.y + TILE * 1.8 + ctx.rng() * TILE
        const e = ctx.spawnEnemy(id, posScratch, { elite: null })
        if (!e.alive) continue
        e.tags.broodgate = i
      }
      emit(m.home.x, m.home.y + 10, {
        count: 10,
        color: '#8a6f9a',
        speed: 110,
        life: 0.35,
        size: 3,
        angle: Math.PI / 2,
        spread: 1.6,
        shape: 1,
      })
      ctx.sfx('generator-die')
    }
  }

  /** P1's signature: a 3-bolt spread at the Warden, always announced. */
  private updateBolts(ctx: RunCtx, dt: number, phase: number): void {
    if (this.boltWind > 0) {
      this.boltWind -= dt
      if (this.boltWind <= 0) this.fireSpread(ctx)
      return
    }
    this.boltT -= dt
    if (this.boltT > 0) return
    this.boltT = byPhase(D.bolt.interval, phase)
    this.boltWind = D.bolt.windup
    this.telegraph('spread', D.bolt.windup)
  }

  private fireSpread(ctx: RunCtx): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const base = Math.atan2(dy, dx)
    const spread = D.bolt.spreadDeg * RAD
    const denom = Math.max(1, D.bolt.count - 1)
    for (let i = 0; i < D.bolt.count; i++) {
      const a = base + (i / denom - 0.5) * spread
      dirScratch.x = Math.cos(a)
      dirScratch.y = Math.sin(a)
      posScratch.x = this.pos.x + dirScratch.x * (this.radius * 0.7)
      posScratch.y = this.pos.y + dirScratch.y * (this.radius * 0.7)
      ctx.spawnProjectile(D.bolt.id, posScratch, dirScratch, false)
    }
    ctx.sfx('shoot')
    emit(this.pos.x, this.pos.y + 8, {
      count: 12,
      color: '#ffd98a',
      speed: 160,
      life: 0.28,
      size: 3,
      angle: base,
      spread,
      shape: 1,
    })
  }

  /**
   * P2's signature: a ray out of the Heart that sweeps the hall. Telegraphed for
   * a full second, and genuinely blocked by the pillars — the lane stops at the
   * first wall, so standing in a pillar's shadow is real cover. P3 sweeps back.
   */
  private updateBeam(ctx: RunCtx, dt: number, phase: number): void {
    const interval = byPhase(D.beam.interval, phase)
    if (this.beamState === 'idle') {
      if (interval <= 0) return
      this.beamT -= dt
      if (this.beamT > 0) return
      this.beamT = interval
      this.beamState = 'wind'
      this.beamWind = D.beam.windup
      this.beamDirIndex = 0
      this.beamSweepsLeft = phase >= 3 ? 2 : 1
      this.beamK = 0
      this.beamAngle = this.sweepAngle(0)
      this.beamLen = this.rayLength(ctx, this.beamAngle)
      this.telegraph('beam', D.beam.windup)
      ctx.bark(phase >= 3 ? 'It sweeps, and sweeps back.' : 'Light in the gate. Get behind stone.')
      return
    }

    if (this.beamState === 'wind') {
      this.beamWind -= dt
      this.beamAngle = this.sweepAngle(0)
      this.beamLen = this.rayLength(ctx, this.beamAngle)
      if (this.beamWind <= 0) {
        this.beamState = 'sweep'
        this.beamK = 0
        ctx.sfx('potion')
        ctx.shake(3, 0.3)
      }
      return
    }

    // sweeping
    this.beamK += dt / D.beam.sweepSeconds
    const k = Math.min(1, this.beamK)
    this.beamAngle = this.sweepAngle(k)
    this.beamLen = this.rayLength(ctx, this.beamAngle)
    this.beamTouch -= dt
    this.beamHit(ctx)
    emit(
      this.pos.x + Math.cos(this.beamAngle) * this.beamLen,
      this.pos.y + Math.sin(this.beamAngle) * this.beamLen,
      { count: 2, color: '#ffb347', speed: 90, life: 0.2, size: 3, shape: 1 },
    )
    if (this.beamK >= 1) {
      this.beamSweepsLeft--
      if (this.beamSweepsLeft > 0) {
        this.beamDirIndex++
        this.beamK = 0
        ctx.sfx('telegraph')
      } else {
        this.beamState = 'idle'
      }
    }
  }

  /** Sweep 0 runs left->right; every following sweep reverses. */
  private sweepAngle(k: number): number {
    const from = D.beam.fromDeg * RAD
    const to = D.beam.toDeg * RAD
    const t = this.beamDirIndex % 2 === 0 ? k : 1 - k
    return from + (to - from) * t
  }

  /** March the ray until it meets a wall — that length is the drawn lane. */
  private rayLength(ctx: RunCtx, angle: number): number {
    const cx = Math.cos(angle)
    const cy = Math.sin(angle)
    const step = 10
    const max = 1600
    for (let d = this.radius; d < max; d += step) {
      const x = this.pos.x + cx * d
      const y = this.pos.y + cy * d
      if (ctx.isWall(Math.floor(x / TILE), Math.floor(y / TILE))) return d
    }
    return max
  }

  private beamHit(ctx: RunCtx): void {
    if (this.beamTouch > 0) return
    const p = ctx.player.pos
    const dx = p.x - this.pos.x
    const dy = p.y - this.pos.y
    const along = dx * Math.cos(this.beamAngle) + dy * Math.sin(this.beamAngle)
    if (along <= 0 || along > this.beamLen) return
    const perp = Math.abs(-dx * Math.sin(this.beamAngle) + dy * Math.cos(this.beamAngle))
    if (perp > D.beam.laneHalf) return
    this.beamTouch = 0.35
    ctx.damagePlayer(D.beam.damage, 'broodgate-beam')
  }

  // ------------------------------------------------------------------ death

  private die(ctx: RunCtx): void {
    this.dying = true
    // nothing it started may outlive it: a death mid-sweep would otherwise leave
    // the beam painted across the hall for as long as the corpse stands
    this.beamState = 'idle'
    ctx.hitstop(D.death.hitstop)
    ctx.shake(D.death.shake, D.death.shakeSeconds)
    ctx.sfx('generator-die')
    ctx.sfx('death')
    for (let i = 0; i < 3; i++) {
      emit(this.pos.x, this.pos.y, {
        count: 40,
        color: i === 0 ? '#d8cfb4' : i === 1 ? '#ff9a3c' : '#8a2a2a',
        speed: 260 + i * 90,
        speedVar: 200,
        life: 0.9,
        lifeVar: 0.5,
        size: 5,
        grav: 180,
        drag: 0.88,
      })
    }
    for (const m of this.maws) {
      if (!m.part) continue
      m.part.alive = false
      emit(m.home.x, m.home.y, {
        count: 20,
        color: '#d8cfb4',
        speed: 210,
        speedVar: 130,
        life: 0.7,
        lifeVar: 0.3,
        size: 4,
        grav: 220,
      })
    }
    // Whatever it poured dies with it: the arena is quiet for the payout.
    const list = ctx.enemies
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i]
      if (e.alive) e.kill(true)
    }

    ctx.featTick('boss1_kill')
    if (!this.tookDamage) {
      ctx.featTick('boss1_nohit')
      ctx.bark('Untouched at the gate.')
    }
    ctx.bark('The Broodgate stops pouring.')
    bossDefeated(ctx)
  }

  // ----------------------------------------------------------------- render

  render(d: Draw): void {
    const ctx = d.ctx
    this.renderGate(ctx)
    this.renderMaws(ctx)
    this.renderHeart(ctx)
    if (this.beamState !== 'idle') this.renderBeam(ctx)
    this.renderTelegraphs(d)
  }

  /**
   * The wall-fused gate: bone ribs and mortar spanning the hall's north end.
   * It fades out downward instead of ending in a hard line — the gate has to sit
   * *behind* the fight, and a solid slab would hide every body that walks out of
   * a maw. The maws and the Heart are drawn on top of it, at full weight.
   */
  private renderGate(ctx: CanvasRenderingContext2D): void {
    const left = TILE * 1
    const right = TILE * 43
    const top = 0
    const bottom = TILE * 5.4

    ctx.save()
    const slab = ctx.createLinearGradient(0, top, 0, bottom)
    slab.addColorStop(0, 'rgba(36,29,36,0.98)')
    slab.addColorStop(0.55, 'rgba(36,29,36,0.82)')
    slab.addColorStop(1, 'rgba(36,29,36,0)')
    ctx.fillStyle = slab
    ctx.fillRect(left, top, right - left, bottom - top)

    ctx.fillStyle = '#3a2f36'
    ctx.fillRect(left, top, right - left, 12)

    const rib = ctx.createLinearGradient(0, top, 0, bottom)
    rib.addColorStop(0, 'rgba(207,195,164,0.85)')
    rib.addColorStop(0.6, 'rgba(207,195,164,0.5)')
    rib.addColorStop(1, 'rgba(207,195,164,0)')
    const ribHi = ctx.createLinearGradient(0, top, 0, bottom)
    ribHi.addColorStop(0, 'rgba(240,232,208,0.95)')
    ribHi.addColorStop(0.6, 'rgba(240,232,208,0.5)')
    ribHi.addColorStop(1, 'rgba(240,232,208,0)')
    for (let x = left + 14; x < right - 12; x += 27) {
      const h = bottom - top - 16 - (((x / 27) | 0) % 3) * 10
      ctx.fillStyle = rib
      ctx.fillRect(x, top + 10, 8, h)
      ctx.fillStyle = ribHi
      ctx.fillRect(x, top + 10, 3, h)
    }
    ctx.restore()
  }

  /**
   * Maws, over the gate: a living maw gets its sprite and a health bar, a dead
   * one leaves a socket that visibly knits itself back together.
   */
  private renderMaws(ctx: CanvasRenderingContext2D): void {
    if (!this.built) return
    const base = byPhase(D.regrowSeconds, this.phase)
    for (const m of this.maws) {
      const part = m.part
      if (part && part.alive) {
        ctx.save()
        ctx.globalAlpha = 0.35
        ctx.fillStyle = '#000000'
        ctx.beginPath()
        ctx.ellipse(m.home.x, m.home.y + 14, 20, 7, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
        drawSprite(ctx, part.spriteId, m.home.x, m.home.y)
        const w = part.radius * 2.6
        const x = m.home.x - w / 2
        const y = m.home.y - part.radius - 14
        ctx.fillStyle = '#00000099'
        ctx.fillRect(x - 1, y - 1, w + 2, 6)
        ctx.fillStyle = '#c9a227'
        ctx.fillRect(x, y, w * Math.max(0, part.hp / part.maxHp), 4)
        continue
      }
      const k = base > 0 ? 1 - Math.max(0, Math.min(1, m.regrow / base)) : 0
      ctx.save()
      ctx.globalAlpha = 0.5
      ctx.fillStyle = '#170f14'
      ctx.beginPath()
      ctx.arc(m.home.x, m.home.y, 18, 0, Math.PI * 2)
      ctx.fill()
      // a dead gate's sockets stay dark — no knitting rings on a corpse
      if (!this.dying) {
        ctx.globalAlpha = 0.85
        ctx.strokeStyle = '#7d6f8f'
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.arc(m.home.x, m.home.y, 15, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * k)
        ctx.stroke()
        ctx.globalAlpha = 0.35 + 0.25 * Math.sin(m.regrow * 6)
        ctx.fillStyle = '#d8cfb4'
        ctx.beginPath()
        ctx.arc(m.home.x, m.home.y, 5 + 7 * k, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
    }
  }

  private renderHeart(ctx: CanvasRenderingContext2D): void {
    const id = this.exposed ? 'broodgate_heart' : 'broodgate_heart_sealed'
    const pulse = this.exposed ? 0.55 + 0.45 * Math.sin(this.hp * 0.02 + performance.now() / 260) : 0.25

    ctx.save()
    ctx.globalAlpha = (this.exposed ? 0.3 : 0.12) * (0.6 + 0.4 * pulse)
    ctx.fillStyle = this.exposed ? '#ff5a3c' : '#6f7a94'
    ctx.beginPath()
    ctx.arc(this.pos.x, this.pos.y, this.radius + 16 + pulse * 6, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()

    drawSprite(ctx, id, this.pos.x, this.pos.y, {
      scale: 1.6,
      white: this.hurtT > 0 ? 0.9 : 0,
    })

    if (this.pingT > 0) {
      ctx.save()
      ctx.globalAlpha = 0.8 * (this.pingT / 0.16)
      ctx.strokeStyle = '#cfd6e4'
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(this.pos.x, this.pos.y, this.radius + 8, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }

    if (!this.exposed && !this.dying) {
      ctx.save()
      ctx.font = '700 11px ui-monospace, "SF Mono", Menlo, monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const need = byPhase(D.exposeAt, this.phase) - this.mawsDown
      ctx.fillStyle = '#000000'
      ctx.fillText(`SEALED — ${need} MORE MAW${need === 1 ? '' : 'S'}`, this.pos.x + 1, this.pos.y + this.radius + 15)
      ctx.fillStyle = '#9aa6c4'
      ctx.fillText(`SEALED — ${need} MORE MAW${need === 1 ? '' : 'S'}`, this.pos.x, this.pos.y + this.radius + 14)
      ctx.restore()
    }
  }

  private renderBeam(ctx: CanvasRenderingContext2D): void {
    const winding = this.beamState === 'wind'
    const a = this.beamAngle
    const len = this.beamLen
    const half = D.beam.laneHalf
    const ex = this.pos.x + Math.cos(a) * len
    const ey = this.pos.y + Math.sin(a) * len

    ctx.save()
    if (winding) {
      // the whole arc it is about to cross, then the lane it starts on
      const k = 1 - Math.max(0, this.beamWind) / D.beam.windup
      ctx.globalAlpha = 0.06 + 0.06 * k
      ctx.fillStyle = '#ffd166'
      ctx.beginPath()
      ctx.moveTo(this.pos.x, this.pos.y)
      ctx.arc(this.pos.x, this.pos.y, len, D.beam.toDeg * RAD, D.beam.fromDeg * RAD)
      ctx.closePath()
      ctx.fill()

      ctx.globalAlpha = 0.25 + 0.45 * k
      ctx.strokeStyle = '#ffd166'
      ctx.lineWidth = half * 2
      ctx.beginPath()
      ctx.moveTo(this.pos.x, this.pos.y)
      ctx.lineTo(ex, ey)
      ctx.stroke()
    } else {
      ctx.globalAlpha = 0.35
      ctx.strokeStyle = '#ff4a2a'
      ctx.lineWidth = half * 2 + 8
      ctx.beginPath()
      ctx.moveTo(this.pos.x, this.pos.y)
      ctx.lineTo(ex, ey)
      ctx.stroke()

      ctx.globalAlpha = 0.95
      ctx.strokeStyle = '#fff3c4'
      ctx.lineWidth = half
      ctx.beginPath()
      ctx.moveTo(this.pos.x, this.pos.y)
      ctx.lineTo(ex, ey)
      ctx.stroke()
    }
    ctx.restore()
  }
}

// ---------------------------------------------------------------- sprites
// Chunky, palette-driven, readable at a glance: a maw is a mouth, the Heart is
// a heart, and the sealed Heart is plated over so "sealed" is a silhouette
// change rather than a colour change.

registerRecipe(ZONE1_BOSS_PROJECTILES[0].id, {
  key: 'broodbolt',
  size: 17,
  params: { color: ZONE1_BOSS_PROJECTILES[0].color },
})
registerRecipe('broodgate_maw_open', {
  key: 'broodgate_maw',
  size: 40,
  params: { bone: '#d8cfb4', maw: '#2b1a24', open: 1 },
})
registerRecipe('broodgate_heart', { key: 'broodgate_heart', size: 44, params: { sealed: 0 } })
registerRecipe('broodgate_heart_sealed', { key: 'broodgate_heart', size: 44, params: { sealed: 1 } })

// ---------------------------------------------------------------- registry

registerZoneContent({
  generators: ZONE1_BOSS_GENERATORS,
  projectiles: ZONE1_BOSS_PROJECTILES,
})
registerBossArena(D.id, ZONE1_BOSS_ARENA)
registerBoss(D.id, () => new Broodgate())
